import type { NavModule } from '@biovet/ui';

export const MODULES: NavModule[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    hint: 'Position at a glance',
    items: [
      { key: 'overview', label: 'Overview', tag: 'OVW', permission: 'dashboard.overview' },
      {
        key: 'cash-position',
        label: 'Cash & Bank Position',
        tag: 'CASH',
        permission: 'dashboard.cash_position',
      },
      {
        key: 'receivables',
        label: 'Receivables Ageing',
        tag: 'AR',
        permission: 'dashboard.receivables',
      },
      { key: 'payables', label: 'Payables Ageing', tag: 'AP', permission: 'dashboard.payables' },
      { key: 'activity', label: 'Recent Activity', tag: 'ACT', permission: 'dashboard.activity' },
    ],
  },
  {
    key: 'brokerage',
    label: 'Brokerage',
    hint: 'Deals, commission on Party A',
    items: [
      { key: 'deal', label: 'Deal Entry', tag: 'DEAL', permission: 'brokerage.deal' },
      {
        key: 'deal-register',
        label: 'Deal Register',
        tag: 'REG',
        permission: 'brokerage.deal_register',
      },
      {
        key: 'deal-parties',
        label: 'Deal Parties',
        tag: 'PTY',
        permission: 'brokerage.deal_parties',
      },
      {
        key: 'availability',
        label: 'Product Availability',
        tag: 'MAP',
        permission: 'brokerage.availability',
      },
      {
        key: 'commission-invoice',
        label: 'Commission Invoice',
        tag: 'COM',
        permission: 'brokerage.commission_invoice',
      },
      {
        key: 'settlement',
        label: 'Pending Settlement',
        tag: 'SET',
        permission: 'brokerage.settlement',
      },
    ],
  },
  {
    key: 'finance',
    label: 'Finance & Accounts',
    hint: 'Vouchers, ledgers, statements',
    items: [
      {
        key: 'coa',
        label: 'Chart of Accounts',
        tag: 'COA',
        permission: 'finance.chart_of_accounts',
      },
      { key: 'jv', label: 'Journal Voucher', tag: 'JV', permission: 'finance.journal_voucher' },
      { key: 'cp', label: 'Cash Payment', tag: 'CP', permission: 'finance.cash_payment' },
      { key: 'cr', label: 'Cash Receipt', tag: 'CR', permission: 'finance.cash_receipt' },
      { key: 'bp', label: 'Bank Payment', tag: 'BP', permission: 'finance.bank_payment' },
      { key: 'br', label: 'Bank Receipt', tag: 'BR', permission: 'finance.bank_receipt' },
      { key: 'gl', label: 'General Ledger', tag: 'GL', permission: 'finance.general_ledger' },
      { key: 'tb', label: 'Trial Balance', tag: 'TB', permission: 'finance.trial_balance' },
      { key: 'pl', label: 'Profit & Loss', tag: 'P&L', permission: 'finance.profit_loss' },
      { key: 'bs', label: 'Balance Sheet', tag: 'BS', permission: 'finance.balance_sheet' },
      {
        key: 'recon',
        label: 'Bank Reconciliation',
        tag: 'REC',
        permission: 'finance.bank_reconciliation',
      },
    ],
  },
  {
    key: 'sales',
    label: 'Sales & Purchase',
    hint: 'Import, sales, commission',
    items: [
      { key: 'shipments', label: 'Import Shipments', tag: 'SHP', permission: 'purchase.shipment' },
      { key: 'costing', label: 'Import Costing Sheet', tag: 'CST', permission: 'purchase.costing' },
      { key: 'purchase', label: 'Purchase Entry', tag: 'PUR', permission: 'purchase.invoice' },
      { key: 'preturn', label: 'Purchase Return', tag: 'PRN', permission: 'purchase.return' },
      { key: 'invoice', label: 'Sales Invoice', tag: 'INV', permission: 'sales.invoice' },
      { key: 'sreturn', label: 'Sale Return', tag: 'SRN', permission: 'sales.return' },
      {
        key: 'commission-run',
        label: 'Commission Run',
        tag: 'RUN',
        permission: 'sales.commission_run',
      },
      { key: 'parties', label: 'Suppliers & Customers', tag: 'PTY', permission: 'master.party' },
    ],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    hint: 'Products, warehouses, stock count',
    items: [
      { key: 'products', label: 'Product Management', tag: 'SKU', permission: 'inventory.product' },
      {
        key: 'categories',
        label: 'Product Categories',
        tag: 'CAT',
        permission: 'inventory.category',
      },
      { key: 'stock', label: 'Warehouse Inventory', tag: 'WH', permission: 'inventory.stock' },
      {
        key: 'stock-ledger',
        label: 'Stock Ledger',
        tag: 'LDG',
        permission: 'inventory.stock_ledger',
      },
      { key: 'transfer', label: 'Stock Transfer', tag: 'TRF', permission: 'inventory.transfer' },
      { key: 'count', label: 'Physical Count', tag: 'CNT', permission: 'inventory.count' },
      { key: 'batch', label: 'Batch & Expiry', tag: 'BCH', permission: 'inventory.batch' },
    ],
  },
  {
    key: 'users',
    label: 'Users & Zones',
    hint: 'Hierarchy-based access control',
    items: [
      { key: 'hierarchy', label: 'User Hierarchy', tag: 'USR', permission: 'admin.user' },
      { key: 'roles', label: 'Roles & Permissions', tag: 'ACL', permission: 'admin.role' },
      { key: 'zones', label: 'Zone Management', tag: 'ZON', permission: 'admin.zone' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    hint: 'Library, ageing, margin, audit trail',
    items: [
      { key: 'library', label: 'Report Library', tag: 'ALL', permission: 'reports.library' },
      { key: 'rpt-ageing', label: 'AR / AP Ageing', tag: 'AGE', permission: 'reports.financial' },
      { key: 'rpt-saleszone', label: 'Sales by Zone', tag: 'ZON', permission: 'reports.sales' },
      { key: 'rpt-margin', label: 'Gross Margin', tag: 'GM', permission: 'reports.management' },
      {
        key: 'rpt-sales-register',
        label: 'Sales Register',
        tag: 'SRG',
        permission: 'reports.sales_register',
      },
      {
        key: 'rpt-purchase-register',
        label: 'Purchase Register',
        tag: 'PRG',
        permission: 'reports.purchase_register',
      },
      { key: 'rpt-audit', label: 'Audit Trail', tag: 'LOG', permission: 'admin.audit' },
    ],
  },
  {
    key: 'setup',
    label: 'Setup',
    hint: 'Company, calendar, numbering, masters',
    items: [
      { key: 'company', label: 'Company Profile', tag: 'CO', permission: 'admin.company' },
      {
        key: 'fy',
        label: 'Financial Year & Periods',
        tag: 'FY',
        permission: 'finance.period_control',
      },
      { key: 'numbering', label: 'Voucher Numbering', tag: 'NUM', permission: 'admin.numbering' },
      { key: 'fx', label: 'Tax & FX Settings', tag: 'FX', permission: 'master.tax' },
      { key: 'branches', label: 'Branches', tag: 'BR', permission: 'admin.branch' },
      { key: 'currencies', label: 'Currencies', tag: 'CUR', permission: 'admin.currency' },
      { key: 'uoms', label: 'Units of Measure', tag: 'UOM', permission: 'admin.uom' },
      { key: 'links', label: 'Link Accounts', tag: 'LNK', permission: 'admin.account_links' },
    ],
  },
];

export function visibleModules(
  modules: NavModule[],
  can: (module: string, action?: string) => boolean,
): NavModule[] {
  const out: NavModule[] = [];
  let changed = false;

  for (const module of modules) {
    const items = module.items.filter((item) => !item.permission || can(item.permission));
    if (items.length === module.items.length) {
      out.push(module);
      continue;
    }
    changed = true;
    if (items.length === 0) continue;
    out.push({ ...module, items, href: items[0]?.href ?? module.href });
  }

  return changed ? out : modules;
}

const BADGE_KEYS = {
  recon: 'recon',
  settlement: 'settlement',
} as const;

export function withBadges(
  modules: NavModule[],
  counts: Partial<Record<keyof typeof BADGE_KEYS, number>> | undefined,
): NavModule[] {
  if (!counts) return modules;

  const byKey = new Map<string, number>();
  for (const [name, navKey] of Object.entries(BADGE_KEYS)) {
    const value = counts[name as keyof typeof BADGE_KEYS];
    if (typeof value === 'number' && value > 0) byKey.set(navKey, value);
  }
  if (byKey.size === 0) return modules;

  return modules.map((module) =>
    module.items.some((item) => byKey.has(item.key))
      ? {
          ...module,
          items: module.items.map((item) =>
            byKey.has(item.key) ? { ...item, badge: byKey.get(item.key) } : item,
          ),
        }
      : module,
  );
}
